import { useParams } from 'react-router-dom'

import { Badge } from '@/components/ui/Badge'
import { Button, ButtonLink } from '@/components/ui/Button'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { ErrorState, Loading, Notice } from '@/components/ui/States'
import { JobTeam } from '@/features/team/JobTeam'
import { ClientDeliverables } from '@/features/deliverables/JobDeliverables'
import { JobCommunications } from '@/features/communications/JobCommunications'
import { JobDocuments } from '@/features/documents/JobDocuments'
import { PortalActions } from '@/features/portal/PortalActions'
import { JOB_KIND_LABEL, jobName } from '@/lib/jobs'
import { formatAgo, formatDateLong, formatDaysUntil, daysUntil } from '@/lib/dates'
import { describeError } from '@/lib/supabase'
import { depositTone } from './JobCard'
import { ProjectDetails } from './ProjectDetails'
import { useJob, useUpdateJob } from './queries'

/**
 * One project, simplified (28 Oct 2026). Jena asked for exactly five things,
 * in this order, and nothing else:
 *
 *   1. Project details  the couple, type, date, venue, package
 *   2. Team             who shoots it, with their agreements
 *   3. Documents        quote, invoices, contract, questionnaire, files
 *   4. Deliverables     what the couple receives, ticked off
 *   5. Communications   the emails and WhatsApps with the couple
 *
 * "View client portal" (top right) opens what the couple sees. The game plan,
 * money fields, internal notes and editing steps were taken off the page; the
 * data is untouched, and a Workflow section will bring the useful parts back.
 */
export function JobDetailPage() {
  const { id } = useParams<{ id: string }>()
  const query = useJob(id)
  const update = useUpdateJob(id ?? '')
  const job = query.data

  if (query.isPending) return <Loading label="Loading the project" />

  if (query.isError || !job) {
    return (
      <Page>
        <ErrorState title="That project could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
      </Page>
    )
  }

  const couple = jobName(job)
  const days = daysUntil(job.event_date)
  const deposit = depositTone(job)

  return (
    <Page>
      <PageHeader
        eyebrow={`${JOB_KIND_LABEL[job.kind]} ${String(job.job_number)}`}
        title={<span className="display">{couple}</span>}
        meta={
          <>
            <time dateTime={job.event_date}>{formatDateLong(job.event_date)}</time>
            <span className={days < 0 ? 'text-taupe' : days <= 7 ? 'text-clay' : days <= 30 ? 'text-amber' : ''}>{formatDaysUntil(job.event_date)}</span>
            <Badge tone={deposit.tone}>{deposit.label}</Badge>
            {job.inquiry ? (
              <ButtonLink to={`/enquiries/${job.inquiry.id}`} variant="ghost" size="sm">
                Original enquiry
              </ButtonLink>
            ) : null}
          </>
        }
        actions={<PortalActions job={job} />}
      />

      {job.archived_at ? (
        <Notice
          className="mb-4"
          tone="neutral"
          title="Archived"
          action={
            <Button size="sm" onClick={() => update.mutate({ archived_at: null })}>
              Restore
            </Button>
          }
        >
          Archived {formatAgo(job.archived_at)}. It is off the board but nothing has been deleted.
        </Notice>
      ) : null}

      {/* Project details on the right, about 30% (28 Oct 2026); first on a phone. */}
      <div className="grid items-start gap-x-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]">
        <div className="min-w-0 lg:order-2">
          <Section title="Project details">
            <ProjectDetails job={job} />
          </Section>
        </div>

        <div className="min-w-0 lg:order-1">
          <Section title="Team" description="Pick who shoots this project. Names save straight away; send each person their agreement when you're ready.">
            <JobTeam jobId={job.id} jobTitle={couple} venue={job.venue_name} eventDate={job.event_date} confirmedAt={job.team_confirmed_at} />
          </Section>

          <Section title="Documents" description="Everything sent to the couple. Drafts and files you keep to yourself never show in their portal.">
            <JobDocuments jobId={job.id} jobTitle={couple} contractSignedAt={job.contract_signed_at} />
          </Section>

          <Section title="Deliverables" description="What the couple receives. Tick each one off as it goes out.">
            <ClientDeliverables jobId={job.id} packageId={job.package_id} job={job} />
          </Section>

          <Section title="Communications">
            <JobCommunications jobId={job.id} />
          </Section>
        </div>
      </div>

      <div className="flex justify-end">
        {!job.archived_at ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (window.confirm('Archive this project? It comes off the board; nothing is deleted and it can be restored.')) {
                update.mutate({ archived_at: new Date().toISOString() })
              }
            }}
          >
            Archive this project
          </Button>
        ) : null}
      </div>
    </Page>
  )
}
